import { FC, useEffect, useMemo, useState } from 'react';
import { useSearch } from '@/hooks';
import { useGetSiteByIdQuery, useSearchSitesQuery } from '@/apis/sites/sitesApi';
import { SelectV2 } from '@data-products-and-ai/react-components';
import { Option } from '@data-products-and-ai/react-components/lib/components/Forms/SelectV2/SelectV2';

import '@/pages/Outages/components/SitesFilter/SitesFilter.scss';

const SITES_LIMIT = 100;

interface SitesFilterProps {
  disabled?: boolean;
  onChange: (siteId: string) => void;
  selectedSiteId?: string | null;
}

export const SitesFilter: FC<SitesFilterProps> = ({
  disabled = false,
  onChange,
  selectedSiteId = null,
}) => {
  const { debouncedQuery, handleSearch } = useSearch();
  const [selectedValues, setSelectedValues] = useState<Option[]>([]);
  const { data, isLoading, isFetching } = useSearchSitesQuery({
    limit: SITES_LIMIT,
    query: debouncedQuery,
  });
  const parsedSelectedSiteId =
    selectedSiteId && Number.isFinite(Number(selectedSiteId))
      ? Number(selectedSiteId)
      : null;
  const { data: selectedSite } = useGetSiteByIdQuery(
    parsedSelectedSiteId ?? 0,
    {
      skip: !parsedSelectedSiteId,
    },
  );
  const sites = (data || []) as Option[];
  const loadingState = isLoading || isFetching;
  const selectedOption = useMemo(() => {
    if (selectedSite) {
      return {
        value: selectedSite.siteId.toString(),
        label: selectedSite.siteName,
      } as Option;
    }

    if (!selectedSiteId) return null;

    return (
      sites.find((site) => site.value === selectedSiteId) ?? null
    );
  }, [selectedSite, selectedSiteId, sites]);

  const handleSiteChange = (values: Option | Option[]) => {
    const site = values as Option;

    setSelectedValues(site ? [site] : []);

    onChange(site?.value ?? null);
  };

  const handleSearchByText = (value: string) => {
    handleSearch(value);
  };

  useEffect(() => {
    if (selectedOption) {
      setSelectedValues([selectedOption]);
      return;
    }

    setSelectedValues([]);
  }, [selectedOption]);

  return (
    <div className="sites-filter">
      <SelectV2
        isDisabled={disabled}
        menuWithCheckIcon
        options={sites}
        isClearable
        isSearchable
        noOptionsMessage={
          loadingState ? 'Searching sites...' : 'No sites found'
        }
        closeMenuOnSelect
        selectedValues={selectedValues}
        placeholder="Select site..."
        onChange={handleSiteChange}
        onInput={handleSearchByText}
      />
    </div>
  );
};
